import { useEffect, useState } from 'react'
import Box from '@cloudscape-design/components/box'
import Select from '@cloudscape-design/components/select'
import SideNavigation from '@cloudscape-design/components/side-navigation'
import SpaceBetween from '@cloudscape-design/components/space-between'
import { useLocation, useNavigate } from 'react-router'
import type { SelectProps } from '@cloudscape-design/components/select'
import type { SideNavigationProps } from '@cloudscape-design/components/side-navigation'
import { useClientsNav } from '@/hooks/useClients'
import { SELECTED_CLIENT_STORAGE_KEY } from '@/lib/clientStorage'
import styles from './SideNav.module.css'

function getActiveSectionHref(pathname: string, clientId: string | null): string {
  if (!clientId) return pathname
  const base = `/workspace/clients/${clientId}`
  if (!pathname.startsWith(base + '/')) return pathname
  const segment = pathname.slice(base.length + 1).split('/')[0]
  if (['configure', 'chat'].includes(segment)) return `${base}/agent`
  if (['history'].includes(segment)) return `${base}/campaigns`
  if (['outreach', 'outreach-campaigns'].includes(segment)) return `${base}/directory`
  return pathname
}

function buildClientLinks(clientId: string): SideNavigationProps.Item[] {
  const baseUrl = `/workspace/clients/${clientId}`
  return [
    { type: 'link', text: 'Approvals', href: `${baseUrl}/queue` },
    { type: 'link', text: 'Agent', href: `${baseUrl}/agent` },
    { type: 'link', text: 'Content', href: `${baseUrl}/campaigns` },
    { type: 'link', text: 'Providers', href: `${baseUrl}/directory` },
  ]
}

export default function SideNav() {
  const navigate = useNavigate()
  const location = useLocation()
  const { clients, loading, error } = useClientsNav()
  const [activeHref, setActiveHref] = useState('#/')

  const [storedClientId, setStoredClientId] = useState<string | null>(() =>
    localStorage.getItem(SELECTED_CLIENT_STORAGE_KEY)
  )

  useEffect(() => {
    setActiveHref(getActiveSectionHref(location.pathname, storedClientId))
  }, [location.pathname, storedClientId])

  useEffect(() => {
    if (loading || clients.length === 0) return
    const isValid = clients.some((c) => c.id === storedClientId)
    if (!isValid) {
      const firstId = clients[0].id
      localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, firstId)
      setStoredClientId(firstId)
    }
  }, [clients, loading, storedClientId])

  const agentOptions: SelectProps.Option[] = clients.map((c) => ({
    label: c.agentName ?? c.brandName,
    value: c.id,
  }))

  const selectedOption = agentOptions.find((o) => o.value === storedClientId) ?? null

  const handleFollow: SideNavigationProps['onFollow'] = (event) => {
    if (!event.detail.external) {
      event.preventDefault()
      setActiveHref(event.detail.href)
      navigate(event.detail.href)
    }
  }

  const handleAgentChange: SelectProps['onChange'] = ({ detail }) => {
    const id = String(detail.selectedOption.value)
    localStorage.setItem(SELECTED_CLIENT_STORAGE_KEY, id)
    setStoredClientId(id)
    navigate(`/workspace/clients/${id}`)
  }

  const navItems: SideNavigationProps['items'] = [
    { type: 'link', text: 'Dashboard', href: '/' },
    ...(storedClientId ? buildClientLinks(storedClientId) : []),
    {
      type: 'section',
      text: 'Configurations',
      defaultExpanded: true,
      items: [
        { type: 'link', text: 'Agents', href: '/configurations/agents' },
        { type: 'link', text: 'Prompts', href: '/configurations/prompts' },
      ],
    },
    { type: 'link', text: 'Settings', href: '/settings/model' },
  ]

  return (
    <SpaceBetween size="xxs">
      <Box padding={{ horizontal: 's', top: 's' }}>
        <Select
          options={agentOptions}
          selectedOption={selectedOption}
          onChange={handleAgentChange}
          placeholder="Select an agent"
          statusType={error ? 'error' : loading ? 'loading' : 'finished'}
          loadingText="Loading agents..."
          errorText="Failed to load agents"
          empty="No agents yet"
        />
      </Box>
      <div className={styles.navigationContainer}>
        <SideNavigation
          header={{ text: 'Sabar', href: '/' }}
          items={navItems}
          activeHref={activeHref}
          onFollow={handleFollow}
        />
      </div>
    </SpaceBetween>
  )
}
